import { isCatalogMiniappId } from "replo-utils/lib/miniappCatalog";
import { z } from "zod";

import { isProductAssetUrl } from "./productAsset";

const announcementUrlSchema = z
  .url()
  .max(2000)
  .regex(/^https?:\/\//, "Use an http(s) URL");

const DASHBOARD_ORIGIN = "https://dashboard.replo.app";
// Project, site and page ids only mean something inside one user's project.
const ID_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24,}|\d{6,})$/i;

function isDashboardHost(hostname: string): boolean {
  return (
    hostname === new URL(DASHBOARD_ORIGIN).hostname ||
    hostname === "localhost" ||
    hostname.endsWith(".replo.me")
  );
}

const appRouteSchema = z
  .string()
  .max(300)
  .regex(/^\/[^?#]*$/, "Use a path like /logos")
  .refine(
    (route) => !route.split("/").some((segment) => ID_SEGMENT.test(segment)),
    {
      message: "Remove project, site and page ids; they only work for you",
    },
  );

export const inAppAnnouncementDestinationSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("app"),
    miniappId: z
      .string()
      .refine(
        (miniappId): boolean => isCatalogMiniappId(miniappId),
        "Unknown app",
      ),
    route: appRouteSchema.nullable(),
  }),
  z.object({
    kind: z.literal("link"),
    url: announcementUrlSchema.refine(
      (url) => !URL.canParse(url) || !isDashboardHost(new URL(url).hostname),
      "Dashboard links only work for you. Paste it into Opens so it can be converted.",
    ),
  }),
]);
export type InAppAnnouncementDestination = z.infer<
  typeof inAppAnnouncementDestinationSchema
>;

/** Turns what an operator pastes (a dashboard link, an outside link, or an app id) into a destination every user can open. */
export function parseInAppAnnouncementDestination(
  text: string,
):
  | { destination: InAppAnnouncementDestination; removedIds: boolean }
  | { error: string } {
  const pasted = text.trim();
  if (isCatalogMiniappId(pasted)) {
    return {
      destination: { kind: "app", miniappId: pasted, route: null },
      removedIds: false,
    };
  }
  if (!URL.canParse(pasted)) {
    return { error: "Paste a full link, starting with https://" };
  }
  const url = new URL(pasted);
  if (!isDashboardHost(url.hostname)) {
    const link = inAppAnnouncementDestinationSchema.safeParse({
      kind: "link",
      url: url.toString(),
    });
    return link.success
      ? { destination: link.data, removedIds: false }
      : { error: "Use an http(s) link" };
  }
  const appPath = /^\/(?:agent\/[^/]+\/miniapp|open)\/(.+)$/.exec(
    url.pathname,
  )?.[1];
  if (!appPath) {
    return {
      error:
        "That page can't be linked for every user. Paste a link to an app screen, or an outside link.",
    };
  }
  const [appId = "", ...segments] = appPath.split("/").filter(Boolean);
  if (!isCatalogMiniappId(appId)) {
    return { error: `"${appId}" isn't an app users can open` };
  }
  const firstId = segments.findIndex((segment) => ID_SEGMENT.test(segment));
  const kept = firstId === -1 ? segments : segments.slice(0, firstId);
  return {
    destination: {
      kind: "app",
      miniappId: appId,
      route: kept.length ? `/${kept.join("/")}` : null,
    },
    removedIds: true,
  };
}

/** An address that works outside the Agent app: it resolves the user's own project on arrival. */
export function getInAppAnnouncementHref(
  destination: InAppAnnouncementDestination,
): string {
  return destination.kind === "link"
    ? destination.url
    : `${DASHBOARD_ORIGIN}/open/${destination.miniappId}${destination.route ?? ""}`;
}
export const inAppAnnouncementSurfaceSchema = z.enum([
  "agent",
  "theme-builder",
]);
export const inAppAnnouncementMediaUploadSchema = z.object({
  mimeType: z.enum([
    "video/webm",
    "video/mp4",
    "image/png",
    "image/jpeg",
    "image/webp",
  ]),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(30 * 1024 * 1024),
});
export const inAppAnnouncementMediaUploadResultSchema = z.object({
  signedUploadUrl: z.url(),
  publicUrl: z.url(),
});
export const inAppAnnouncementEventSchema = z.enum([
  "viewed",
  "engaged",
  "dismissed",
]);

export const inAppAnnouncementTypeSchema = z.enum(["feature_release"]);

const inAppAnnouncementContentSchema = z.object({
  type: inAppAnnouncementTypeSchema,
  slug: z
    .string()
    .min(1)
    .max(160)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  name: z.string().trim().min(1).max(200),
  blurb: z.string().trim().min(1).max(500),
  destination: inAppAnnouncementDestinationSchema,
  imageUrl: announcementUrlSchema.nullable(),
  videoUrl: announcementUrlSchema.nullable(),
  /** The feature row: the pull requests that shipped this capability. */
  sourcePrs: z
    .array(
      z
        .string()
        .max(500)
        .regex(
          /^https:\/\/github\.com\/[\w.-]+\/[\w.-]+\/pull\/\d+$/,
          "Use a GitHub pull request URL",
        ),
    )
    .max(20),
  /** The feature row: Product Assets captured for it, as their shelf URLs. */
  assetUrls: z
    .array(z.url().refine(isProductAssetUrl, "Use a Product Assets URL"))
    .max(60),
  ctaLabel: z.string().trim().min(1).max(80),
  status: z.enum(["draft", "published", "archived"]),
  publishedAt: z.iso.datetime().nullable(),
  endsAt: z.iso.datetime().nullable(),
  audience: z.enum(["all", "agent", "theme-builder"]),
  showInApp: z.boolean(),
  internalOnly: z.boolean(),
  sortOrder: z.number().int().min(0).max(100_000),
});

export const inAppAnnouncementSchema = inAppAnnouncementContentSchema.extend({
  // A Publisher that deployed before these columns leaves them out, and Admin must still list.
  sourcePrs: inAppAnnouncementContentSchema.shape.sourcePrs.default([]),
  assetUrls: inAppAnnouncementContentSchema.shape.assetUrls.default([]),
  id: z.uuid(),
  legacyResourceId: z.string().min(1).max(100).nullable(),
  resetKey: z.uuid(),
  resetAt: z.iso.datetime(),
  flagKey: z.string().min(1),
  createdBy: z.string().min(1).max(255),
  updatedAt: z.iso.datetime(),
  /** Unique people since the last reannounce. Only the Admin list asks for it. */
  engagement: z
    .object({
      viewed: z.number().int(),
      engaged: z.number().int(),
      dismissed: z.number().int(),
    })
    .optional(),
});

export const inAppAnnouncementInputSchema = inAppAnnouncementContentSchema
  .extend({
    type: inAppAnnouncementContentSchema.shape.type.default("feature_release"),
    // Omitted on create: the service derives a unique one from the name.
    slug: inAppAnnouncementContentSchema.shape.slug.optional(),
    imageUrl: inAppAnnouncementContentSchema.shape.imageUrl.default(null),
    videoUrl: inAppAnnouncementContentSchema.shape.videoUrl.default(null),
    // Left out means none on create and unchanged on update.
    sourcePrs: inAppAnnouncementContentSchema.shape.sourcePrs.optional(),
    assetUrls: inAppAnnouncementContentSchema.shape.assetUrls.optional(),
    ctaLabel:
      inAppAnnouncementContentSchema.shape.ctaLabel.default("Try it now"),
    status: inAppAnnouncementContentSchema.shape.status.default("draft"),
    publishedAt: inAppAnnouncementContentSchema.shape.publishedAt.default(null),
    endsAt: inAppAnnouncementContentSchema.shape.endsAt.default(null),
    audience: inAppAnnouncementContentSchema.shape.audience.default("all"),
    showInApp: inAppAnnouncementContentSchema.shape.showInApp.default(false),
    internalOnly:
      inAppAnnouncementContentSchema.shape.internalOnly.default(true),
    sortOrder: inAppAnnouncementContentSchema.shape.sortOrder.default(0),
  })
  .superRefine((value, context) => {
    if (
      value.endsAt &&
      value.publishedAt &&
      Date.parse(value.endsAt) <= Date.parse(value.publishedAt)
    ) {
      context.addIssue({
        code: "custom",
        path: ["endsAt"],
        message: "End date must follow the publish date",
      });
    }
  });

const inAppAnnouncementUpdateSchema = inAppAnnouncementInputSchema.safeExtend({
  ...inAppAnnouncementContentSchema.shape,
  type: inAppAnnouncementContentSchema.shape.type.default("feature_release"),
  // Left out means unchanged, so an editor that does not send these lists cannot wipe them.
  sourcePrs: inAppAnnouncementContentSchema.shape.sourcePrs.optional(),
  assetUrls: inAppAnnouncementContentSchema.shape.assetUrls.optional(),
});

export const inAppAnnouncementSaveSchema = z.union([
  z.object({
    id: z.never().optional(),
    input: inAppAnnouncementInputSchema,
    expectedUpdatedAt: z.iso.datetime().optional(),
  }),
  z.object({
    id: z.uuid(),
    input: inAppAnnouncementUpdateSchema,
    expectedUpdatedAt: z.iso.datetime(),
  }),
]);

export const inAppAnnouncementCardSchema = inAppAnnouncementSchema
  .pick({
    id: true,
    slug: true,
    name: true,
    blurb: true,
    destination: true,
    imageUrl: true,
    videoUrl: true,
    ctaLabel: true,
    resetKey: true,
  })
  .extend({ dismissed: z.boolean() });

export const inAppAnnouncementRecordSchema = z.object({
  announcementId: z.uuid(),
  resetKey: z.uuid(),
  event: inAppAnnouncementEventSchema,
  surface: inAppAnnouncementSurfaceSchema,
});

export const inAppAnnouncementReportSchema = z.object({
  resetKey: z.uuid(),
  resetKeys: z.array(z.object({ resetKey: z.uuid(), viewed: z.number() })),
  viewed: z.number(),
  engaged: z.number(),
  dismissed: z.number(),
  total: z.number(),
  users: z.array(
    z.object({
      userId: z.uuid(),
      email: z.email(),
      name: z.string().nullable(),
      viewedAt: z.iso.datetime(),
      engagedAt: z.iso.datetime().nullable(),
      dismissedAt: z.iso.datetime().nullable(),
      tags: z.array(z.string()),
    }),
  ),
});

export const inAppAnnouncementImportSchema = z.object({
  announcements: z
    .array(
      z.object({
        id: z.string().min(1).max(100),
        name: z.string().min(1).max(200),
        url: announcementUrlSchema,
        blurb: z.string().min(1).max(500),
        imageUrl: announcementUrlSchema.nullish(),
        publishedAt: z.iso.datetime().nullish(),
        sortOrder: z.number().int().min(0).max(100_000).default(0),
        createdBy: z.string().min(1).max(255),
      }),
    )
    .max(1000),
});

export const inAppAnnouncementVisibilitySchema = z.enum([
  "draft",
  "team",
  "users",
]);
export type InAppAnnouncementVisibility = z.infer<
  typeof inAppAnnouncementVisibilitySchema
>;

/** The one question an operator answers; status, internalOnly and showInApp are its stored form. */
export function getInAppAnnouncementVisibility({
  status,
  internalOnly,
}: {
  status: "archived" | "draft" | "published";
  internalOnly: boolean;
}): InAppAnnouncementVisibility | "archived" {
  if (status !== "published") {
    return status;
  }
  return internalOnly ? "team" : "users";
}

export function getInAppAnnouncementVisibilityFields({
  visibility,
  catalogOnly = false,
}: {
  visibility: InAppAnnouncementVisibility;
  catalogOnly?: boolean;
}) {
  if (visibility === "draft") {
    return { status: "draft", internalOnly: true, showInApp: false } as const;
  }
  return {
    status: "published",
    internalOnly: visibility === "team",
    showInApp: !catalogOnly,
  } as const;
}

export function getInAppAnnouncementSlug(name: string): string {
  return (
    name
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-|-$/g, "")
      .slice(0, 150) || "announcement"
  );
}

export type InAppAnnouncement = z.infer<typeof inAppAnnouncementSchema>;
export type InAppAnnouncementInput = z.infer<
  typeof inAppAnnouncementInputSchema
>;
export type InAppAnnouncementCard = z.infer<typeof inAppAnnouncementCardSchema>;
export type InAppAnnouncementReport = z.infer<
  typeof inAppAnnouncementReportSchema
>;
